import { useState } from 'react';
import type { ReactNode } from 'react';
import { T, AppHeader, ConfirmSheet } from '../../design';
import type { ToastData } from '../../design';
import { usePortal } from '../../context/PortalContext';
import type { Screen, NavStack } from '../../portal/navigation';
import type { Course, EnrollmentWithCourse, SalonEnrollment, SalonEmployee } from '../../types/courses.types';
import {
  useCourses, useCourseDetail, useMyEnrollments, useEnrollment,
  useSalonEnrollments, useSalonEmployees, useBulkEnrollment,
} from '../../hooks/courses';
import { useSalonApprovals } from '../../hooks/courses/useSalonApprovals';
import { CourseList } from './screens/CourseList';
import { CourseDetail } from './screens/CourseDetail';
import { MyEnrollments } from './screens/MyEnrollments';
import { TabBar } from './screens/TabBar';
import type { CourseTab } from './screens/TabBar';
import { SalonOverview } from './screens/SalonOverview';
import { ApprovalQueue } from './screens/ApprovalQueue';
import { BulkEnroll } from './screens/BulkEnroll';
import { BulkResultSheet } from './screens/BulkResultSheet';
import type { BulkResultView } from './screens/BulkResultSheet';
import { Skeletons, ErrorState } from './screens/states';

export interface CoursesModuleProps {
  screen: Screen;
  nav: NavStack;
  fireToast: (toast: ToastData) => void;
}

const MAIN_PAD = '18px 14px calc(36px + env(safe-area-inset-bottom))';

export function CoursesModule({ screen, nav, fireToast }: CoursesModuleProps) {
  const portal = usePortal();
  const employeeId = portal.employee?.id ?? null;
  const role: 'owner' | 'member' = portal.isOwner ? 'owner' : 'member';
  const isAuthenticated = portal.isAuthenticated && employeeId != null;

  const [tab, setTab] = useState<CourseTab>('list');
  const [showPast, setShowPast] = useState(false);
  const [confirm, setConfirm] = useState<null | { kind: 'cancel-mine'; en: EnrollmentWithCourse } | { kind: 'cancel-bulk'; rows: SalonEnrollment[] }>(null);
  const [bulkResult, setBulkResult] = useState<BulkResultView | null>(null);

  const detailSlug = (screen.type === 'course-detail' || screen.type === 'courses-bulk') ? (screen.slug as string) : undefined;

  const courses = useCourses(employeeId, isAuthenticated, showPast);
  const detail = useCourseDetail(employeeId, detailSlug, isAuthenticated);
  const mine = useMyEnrollments(employeeId, isAuthenticated);
  const enrollment = useEnrollment(employeeId);
  const salonEnrollments = useSalonEnrollments(employeeId, role === 'owner', isAuthenticated);
  const salonEmployees = useSalonEmployees(employeeId, role === 'owner', isAuthenticated);
  const approvals = useSalonApprovals(employeeId, role === 'owner', isAuthenticated);
  const bulk = useBulkEnrollment(employeeId);

  const empName = (id: number) => salonEmployees.employees.find((e) => e.id === id)?.name ?? `Medarbejder ${id}`;

  async function handleEnroll(c: Course) {
    const r = await enrollment.enroll(c.slug);
    if (!r.success) { fireToast({ tone: 'error', msg: r.error ?? 'Fejl ved tilmelding' }); return; }
    if (detail.course?.slug === c.slug) {
      detail.updateEnrollment({ id: r.enrollmentId!, status: r.status!, requestedAt: '', processedAt: null });
      detail.updateEnrolledCount(1);
    }
    fireToast({ tone: 'success', msg: r.status === 'approved' ? 'Du er tilmeldt' : 'Tilmelding sendt — afventer godkendelse' });
    courses.refresh();
  }

  async function doCancelMine(en: EnrollmentWithCourse) {
    const r = await enrollment.cancel(en.id);
    if (!r.success) { fireToast({ tone: 'error', msg: r.error ?? 'Fejl ved afmelding' }); return; }
    fireToast({ tone: 'success', msg: r.newStatus === 'awaiting_credit' ? 'Afmeldt — afventer kreditnota' : 'Afmeldt' });
    mine.refresh();
  }

  async function handleApprove(e: SalonEnrollment) {
    const r = await approvals.approve(e.id);
    fireToast(r.success ? { tone: 'success', msg: `${e.employeeName} godkendt` } : { tone: 'error', msg: r.error ?? 'Fejl' });
    salonEnrollments.refresh();
  }
  async function handleReject(e: SalonEnrollment) {
    const r = await approvals.reject(e.id);
    fireToast(r.success ? { tone: 'info', msg: `${e.employeeName} afvist` } : { tone: 'error', msg: r.error ?? 'Fejl' });
    salonEnrollments.refresh();
  }

  async function handleBulkEnroll(ids: number[]) {
    if (!detail.course) return;
    const r = await bulk.enrollEmployees(detail.course.slug, ids);
    if (!r.success || !r.data) { fireToast({ tone: 'error', msg: r.error ?? 'Fejl ved tilmelding' }); return; }
    setBulkResult({
      kind: 'enroll',
      okCount: r.data.totalEnrolled,
      enrolledNames: r.data.enrollments.map((e) => empName(e.employeeId)),
      skipped: r.data.skipped.map((s) => ({ name: empName(s.employeeId), reason: s.reason })),
    });
    salonEnrollments.refresh();
  }

  async function doBulkCancel(rows: SalonEnrollment[]) {
    const r = await bulk.cancelEnrollments(rows.map((e) => e.id));
    if (!r.success || !r.data) { fireToast({ tone: 'error', msg: r.error ?? 'Fejl ved afmelding' }); return; }
    setBulkResult({
      kind: 'cancel',
      okCount: r.data.totalCancelled,
      awaitingCredit: r.data.awaitingCreditCount,
      cancelled: r.data.cancelled.map((c) => ({ name: c.employeeName, awaitingCredit: c.newStatus === 'awaiting_credit' })),
      skipped: r.data.skipped.map((s) => ({ name: `Tilmelding ${s.enrollmentId}`, reason: s.reason })),
    });
    salonEnrollments.refresh();
  }

  let title = 'Kurser';
  if (screen.type === 'course-detail') title = detail.course?.productName ?? 'Kursus';
  else if (screen.type === 'courses-bulk') title = 'Tilmeld hold';

  let body: ReactNode = null;

  if (screen.type === 'course-detail') {
    if (detail.isLoading) body = <Skeletons n={1} />;
    else if (detail.error) body = <ErrorState onRetry={detail.refresh} message={detail.error} />;
    else if (detail.course) body = <CourseDetail course={detail.course} role={role} myEnrollment={detail.myEnrollment} onEnroll={handleEnroll} onBulk={(c) => nav.push({ type: 'courses-bulk', slug: c.slug })} />;
  } else if (screen.type === 'courses-bulk') {
    if (detail.isLoading || salonEmployees.isLoading) body = <Skeletons n={2} />;
    else if (detail.error || salonEmployees.error) body = <ErrorState onRetry={() => { detail.refresh(); salonEmployees.refresh(); }} message={detail.error ?? salonEmployees.error ?? undefined} />;
    else if (detail.course) body = <BulkEnroll course={detail.course} employees={salonEmployees.employees as SalonEmployee[]} submitting={bulk.isEnrolling} onSubmit={handleBulkEnroll} />;
  } else {
    if (tab === 'list') {
      if (courses.isLoading) body = <Skeletons />;
      else if (courses.error) body = <ErrorState onRetry={courses.refresh} message={courses.error} />;
      else body = <CourseList courses={courses.courses} showPast={showPast} onTogglePast={() => setShowPast((s) => !s)} onOpen={(c) => nav.push({ type: 'course-detail', slug: c.slug })} />;
    } else if (tab === 'mine') {
      if (mine.isLoading) body = <Skeletons />;
      else if (mine.error) body = <ErrorState onRetry={mine.refresh} message={mine.error} />;
      else body = <MyEnrollments enrollments={mine.enrollments} onCancel={(en) => setConfirm({ kind: 'cancel-mine', en })} />;
    } else if (tab === 'salon') {
      if (salonEnrollments.isLoading) body = <Skeletons />;
      else if (salonEnrollments.error) body = <ErrorState onRetry={salonEnrollments.refresh} message={salonEnrollments.error} />;
      else body = <SalonOverview enrollments={salonEnrollments.enrollments} onBulkCancel={(rows) => setConfirm({ kind: 'cancel-bulk', rows })} />;
    } else {
      if (approvals.isLoading) body = <Skeletons />;
      else if (approvals.error) body = <ErrorState onRetry={approvals.refresh} message={approvals.error} />;
      else body = <ApprovalQueue pending={approvals.pending} onApprove={handleApprove} onReject={handleReject} />;
    }
  }

  const showTabs = screen.type === 'kurser';

  return (
    <>
      <AppHeader eyebrow="Kurser" title={title} onBack={nav.back} />
      {showTabs && <TabBar tab={tab} onTab={setTab} role={role} pendingCount={approvals.pending.length} />}
      <main style={{ flex: 1, padding: MAIN_PAD, background: T.bg }}>{body}</main>

      {confirm?.kind === 'cancel-mine' && (
        <ConfirmSheet
          title="Afmeld kursus?"
          message={`Vil du afmelde "${confirm.en.courseProductName}"?`}
          note={confirm.en.coursePrice > 0 ? 'Betalte kurser afmeldes med kreditnota fra økonomi.' : undefined}
          confirmLabel="Afmeld"
          onConfirm={() => doCancelMine(confirm.en)}
          onClose={() => setConfirm(null)}
        />
      )}
      {confirm?.kind === 'cancel-bulk' && (
        <ConfirmSheet
          title={`Afmeld ${confirm.rows.length} tilmeldinger?`}
          message="Medarbejderne afmeldes kurset."
          confirmLabel="Afmeld"
          onConfirm={() => doBulkCancel(confirm.rows)}
          onClose={() => setConfirm(null)}
        />
      )}
      {bulkResult && <BulkResultSheet result={bulkResult} onClose={() => { setBulkResult(null); if (screen.type === 'courses-bulk') nav.back(); }} />}
    </>
  );
}
